import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import LoadingOverlay from 'react-loading-overlay'
import {AdminDataTable, AdminTableSearch} from '../../components/AdminDataTable/AdminDataTable'
import {OrderDetail} from '../../components/OrderDetail/OrderDetail'
import './Pedidos.css'

const money = new Intl.NumberFormat('es-MX', {style: 'currency', currency: 'MXN'})
const dateTime = new Intl.DateTimeFormat('es-MX', {dateStyle: 'medium', timeStyle: 'short'})

function Pedidos() {
  const API_URL = process.env.REACT_APP_API_URL
  const [pedidos, setPedidos] = useState([])
  const [detalle, setDetalle] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({fecha_inicial: '', fecha_final: ''})

  const loadOrders = async (activeFilters = filters) => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${API_URL}/pedidos`, {params: activeFilters})
      setPedidos(data.data || [])
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No fue posible cargar los pedidos.')
    } finally {
      setLoading(false)
    }
  }

  const viewOrder = async (id) => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${API_URL}/pedidos/${id}`)
      setDetalle(data.data)
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No fue posible consultar el pedido.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders({fecha_inicial: '', fecha_final: ''})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateFilter = (event) => setFilters({...filters, [event.target.name]: event.target.value})
  const clearFilters = () => {
    const empty = {fecha_inicial: '', fecha_final: ''}
    setFilters(empty)
    loadOrders(empty)
  }

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es-MX')
    if (!term) return pedidos
    return pedidos.filter((pedido) =>
      `${pedido.folio} ${pedido.distribuidor} ${pedido.nombreComercial}`
        .toLocaleLowerCase('es-MX')
        .includes(term)
    )
  }, [pedidos, search])

  const columns = [
    {name: 'Folio', selector: (row) => row.folio, sortable: true, cell: (row) => <strong className='pedido-folio'>{row.folio}</strong>},
    {id: 'fecha', name: 'Fecha', selector: (row) => new Date(row.fecha.replace(' ', 'T')).getTime(), sortable: true, cell: (row) => dateTime.format(new Date(row.fecha.replace(' ', 'T'))), grow: 1.35},
    {name: 'Distribuidor', selector: (row) => row.distribuidor || '', sortable: true, wrap: true, grow: 1.35},
    {name: 'Vendedor asignado', selector: (row) => row.vendedorAsignadoNombre || '', sortable: true, wrap: true, grow: 1.25, cell: (row) => row.vendedorAsignadoNombre || 'Información no disponible'},
    {name: 'Productos', selector: (row) => Number(row.productos), sortable: true, center: true, compact: true},
    {name: 'Total', selector: (row) => Number(row.total), sortable: true, right: true, cell: (row) => money.format(row.total)},
    {name: 'Acción', button: true, cell: (row) => <button type='button' className='btn btn-sm btn-light-primary' onClick={() => viewOrder(row.id)}>Ver</button>},
  ]

  return (
    <LoadingOverlay active={loading} spinner text='Cargando...'>
      <OrderDetail detail={detalle} onHide={() => setDetalle(null)} />

      <div className='card card-custom card-stretch shadow mb-5 admin-list-card'>
        <div className='card-header'><h3 className='card-title'>Pedidos</h3></div>
        <div className='card-body'>
          <form className='pedido-filters' onSubmit={(event) => {event.preventDefault(); loadOrders()}}>
            <input className='form-control' type='date' name='fecha_inicial' value={filters.fecha_inicial} onChange={updateFilter} aria-label='Fecha inicial' />
            <input className='form-control' type='date' name='fecha_final' value={filters.fecha_final} onChange={updateFilter} aria-label='Fecha final' />
            <button className='btn btn-primary' type='submit'>Filtrar</button>
            <button className='btn btn-light' type='button' onClick={clearFilters}>Limpiar</button>
          </form>
          {error && <div className='alert alert-danger'>{error}</div>}
          <AdminTableSearch value={search} onChange={(event) => setSearch(event.target.value)} ariaLabel='Buscar pedidos' />
          <AdminDataTable
              columns={columns}
              data={filteredOrders}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              defaultSortFieldId='fecha'
              defaultSortAsc={false}
              persistTableHead
              responsive
              striped
              highlightOnHover
              noDataComponent={search ? 'No se encontraron pedidos para la búsqueda.' : 'No se encontraron pedidos.'}
          />
        </div>
      </div>
    </LoadingOverlay>
  )
}

export {Pedidos}
